import { Accept } from 'react-dropzone'

import { Box } from '@factorialco/deprecated-design-system'

import BoxList from 'modules/core/components/BoxList'
import DropZone from 'modules/core/components/DropZone'
import Loading from 'modules/core/components/Loading'
import type { Option } from 'modules/core/components/Popover/Content'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import { useValidationsProvider } from 'modules/processes/components/ItemWizard/Providers/validations'
import { acceptedContentTypesByTasks } from 'modules/tasks/components/TaskForm'

import FileListItem from './FileListItem'
import { TaskConfigurationAttachments } from './types'

export type FileUpdates = {
  filesToAdd: File[]
  filesToRemove: Set<string>
}

export interface IPreviewData {
  currentFiles?: TaskConfigurationAttachments[]
  filesToAdd: File[]
  filesToRemove: Set<string>
}

type Props = {
  files: FilesProps
  onFileUpdates: (fileUpdates: FileUpdates) => void
  acceptedTypes?: Accept
  multiple?: boolean
  mandatory?: boolean
  setCustomPreviewData?: (preview: IPreviewData) => void
}

export interface FilesProps {
  loadingFiles: boolean
  filesToAdd: File[]
  currentFiles: Array<TaskConfigurationAttachments> | undefined
  filesToRemove: Set<string>
  onAddFiles: (files: File[]) => void
  onDeleteNewFile: (fileToDelete: File) => void
  onDeleteExistingFile: (file: { id: string }) => void
}

const BaseList = ({
  files,
  onFileUpdates,
  acceptedTypes,
  mandatory,
  setCustomPreviewData,
  multiple = true,
}: Props) => {
  const { setPreviewValues } = usePreviewProvider()
  const { form } = useFormProvider()
  const { setFieldValidation, removeFieldValidation } = useValidationsProvider()

  const {
    loadingFiles,
    currentFiles,
    filesToAdd,
    filesToRemove,
    onAddFiles,
    onDeleteNewFile,
    onDeleteExistingFile,
  } = files

  useUnsafeEffect(() => {
    onFileUpdates({ filesToAdd, filesToRemove })
  }, [filesToAdd, filesToRemove])

  useUnsafeEffect(() => {
    if (form.values.files) {
      const { filesToAdd: newFilesToAdd, filesToRemove: newFilesToRemove } = form.values.files

      if (newFilesToAdd.length || newFilesToRemove.size) {
        onAddFiles(newFilesToAdd)
      }
    }
  }, [])

  useUnsafeEffect(() => {
    if (setCustomPreviewData) {
      setCustomPreviewData({ currentFiles, filesToAdd, filesToRemove })
    } else {
      setPreviewValues((prevValues) => {
        return { ...prevValues, currentFiles, filesToAdd, filesToRemove }
      })
    }
  }, [currentFiles, filesToAdd, filesToRemove])

  useUnsafeEffect(() => {
    if (mandatory) {
      setFieldValidation({
        field: 'files',
        validation: () => !!currentFiles?.length || !!filesToAdd.length,
      })
    }

    return () => {
      removeFieldValidation('files')
    }
  }, [mandatory, currentFiles, filesToAdd])

  const existingFileOptions = (file: TaskConfigurationAttachments): Option[] => [
    {
      href: buildUrl(file.path),
      title: i18n.t('common.download_file_name', { name: file.filename }),
    },
    {
      onClick: () => onDeleteExistingFile(file),
      title: i18n.t('common.delete'),
      type: 'wrong',
    },
  ]

  const newFileOptions = (file: File): Option[] => [
    {
      onClick: () => onDeleteNewFile(file),
      title: i18n.t('common.delete'),
      type: 'wrong',
    },
  ]

  if (loadingFiles) {
    return <Loading />
  }

  const filesCount = (currentFiles?.length ?? 0) + filesToAdd.length

  return (
    <>
      <DropZone
        multiple={multiple}
        accept={acceptedTypes || acceptedContentTypesByTasks()}
        onDrop={onAddFiles}
      />
      {filesCount > 0 && (
        <Box paddingTop="s24">
          <BoxList>
            {currentFiles
              ?.filter((file) => !filesToRemove.has(file.id))
              .map((file, index) => (
                <FileListItem key={index} file={file} options={existingFileOptions(file)} />
              ))}
            {filesToAdd.map((file, index) => (
              <FileListItem key={index} file={file} options={newFileOptions(file)} />
            ))}
          </BoxList>
        </Box>
      )}
    </>
  )
}

export default BaseList
